import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import TrialOutreachPill from '../../trial-outreach/components/TrialOutreachPill';
import { SetAdminProspectsStatusAPI } from '../../../../api/AdminAPI';
import ProspectsTable from './ProspectsTable';
import { BULK_MAX, TODAY_GROUPS } from '../prospectOptions';

const CLOSE_MAX = 200;
const buttonSx = { borderRadius: '8px', textTransform: 'none', fontWeight: 600 };

/**
 * Um grupo da fila: título, contagem, explicação, a tabela e a ação em lote sobre os
 * marcados (por padrão todos os que têm WhatsApp, até o limite da ação).
 * @param {Object} props
 * @param {{ value: string, title: string, tone: string, description: string }} props.group
 * @param {Array<Object>} props.items
 * @param {{ label: string, icon: Object, limit: number, hint?: string, loading?: boolean, onClick: Function }|null} props.action
 * @param {Function} props.onMessage
 * @param {Function} props.onEdit
 */
function TodayGroup({ group, items, action, onMessage, onEdit }) {
  const withWhatsApp = useMemo(() => items.filter((item) => item.whatsapp), [items]);
  const idsKey = withWhatsApp.map((item) => item.id).join(',');
  const limit = action ? action.limit : 0;
  const [selectedIds, setSelectedIds] = useState([]);

  // A lista mudou (carregou, alguém saiu do grupo): volta a marcar todos, até o limite
  useEffect(() => {
    setSelectedIds(withWhatsApp.slice(0, Math.max(0, limit)).map((item) => item.id));
  }, [idsKey, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = withWhatsApp.filter((item) => selectedIds.includes(item.id));
  const over = action ? selected.length - action.limit : 0;

  const toggle = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  const toggleAll = (checked) => setSelectedIds(checked ? withWhatsApp.map((item) => item.id) : []);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{group.title}</Typography>
        <TrialOutreachPill tone={items.length ? group.tone : 'grey'} label={String(items.length)} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{group.description}</Typography>

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 0.5 }}>
          Ninguém aqui hoje.
        </Typography>
      ) : (
        <>
          <ProspectsTable
            items={items}
            isLoading={false}
            error={null}
            selectable={!!action}
            selectedIds={selectedIds}
            onToggle={toggle}
            onToggleAll={toggleAll}
            onMessage={onMessage}
            onEdit={onEdit}
          />
          {action && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mt: 1.25, px: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {selected.length === 1 ? '1 marcado' : `${selected.length} marcados`}
                {action.hint ? ` · ${action.hint}` : ''}
              </Typography>
              {over > 0 && (
                <Typography variant="body2" color="error">
                  Até {action.limit} por vez. Desmarque {over}.
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                variant="contained"
                startIcon={action.icon}
                disabled={selected.length === 0 || over > 0 || !!action.loading}
                onClick={() => action.onClick(selected)}
                sx={buttonSx}
              >
                {action.label}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

/**
 * ProspectTodayView - Fila do dia da Prospecção (list.php?view=today), nos quatro grupos
 * de TODAY_GROUPS e na ordem em que vale agir: quem respondeu (só ações da linha), retorno
 * vencido (lote com o texto "Retorno"), encerrar (viram "Sem resposta") e novos (primeira
 * mensagem, dentro da cota do dia: BULK_MAX menos o que já saiu hoje).
 * @param {Object} props
 * @param {Array<Object>} props.items só os que pedem ação hoje, com today_group
 * @param {boolean} props.isLoading
 * @param {string|null} props.error
 * @param {number} props.sentToday mensagens que já saíram hoje
 * @param {Function} props.onMessage abre a mensagem um a um
 * @param {Function} props.onEdit
 * @param {Function} props.onBulk recebe (prospects, distribution) para o lote
 * @param {Function} props.onUpdated recebe cada contato que mudou de situação
 * @param {Function} props.onNotice recebe o texto do aviso de sucesso
 */
function ProspectTodayView({ items, isLoading, error, sentToday, onMessage, onEdit, onBulk, onUpdated, onNotice }) {
  const [closing, setClosing] = useState(null);
  const [closingBusy, setClosingBusy] = useState(false);
  const [closeError, setCloseError] = useState('');
  const quota = Math.max(0, BULK_MAX - sentToday);

  const byGroup = useMemo(() => {
    const groups = {};
    TODAY_GROUPS.forEach((group) => { groups[group.value] = []; });
    items.forEach((item) => {
      if (groups[item.today_group]) groups[item.today_group].push(item);
    });
    return groups;
  }, [items]);

  const handleClose = () => {
    setClosingBusy(true);
    setCloseError('');
    SetAdminProspectsStatusAPI({ prospect_ids: closing.map((item) => item.id), status: 'no_reply' }, (response) => {
      setClosingBusy(false);
      if (!response?.success) {
        setCloseError(response?.error || 'Não foi possível mudar a situação.');
        return;
      }
      (response.items || []).forEach(onUpdated);
      onNotice(`${response.changed} contato(s) marcado(s) como Sem resposta.`);
      setClosing(null);
    });
  };

  const actions = {
    replied: null,
    follow_up: {
      label: 'Enviar o Retorno aos marcados',
      icon: <SendOutlinedIcon fontSize="small" />,
      limit: BULK_MAX,
      onClick: (selected) => onBulk(selected, 'retorno'),
    },
    close: {
      label: 'Marcar como Sem resposta',
      icon: <DoNotDisturbOnOutlinedIcon fontSize="small" />,
      limit: CLOSE_MAX,
      onClick: (selected) => { setCloseError(''); setClosing(selected); },
    },
    new: {
      label: 'Enviar a primeira mensagem aos marcados',
      icon: <SendOutlinedIcon fontSize="small" />,
      limit: quota,
      hint: sentToday > 0 ? `já saíram ${sentToday} hoje, cabem mais ${quota}` : `cabem ${quota} hoje`,
      onClick: (selected) => onBulk(selected, 'split'),
    },
  };

  if (isLoading) {
    return (
      <Paper sx={{ borderRadius: '12px', border: '1px solid #E8ECF0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', py: 6, textAlign: 'center' }}>
        <CircularProgress size={32} />
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {!error && items.length === 0 && (
        <Alert severity="success" variant="outlined" sx={{ borderRadius: '12px', bgcolor: '#fff' }}>
          Nada pendente hoje. Busque ou importe contatos novos para a fila de amanhã.
        </Alert>
      )}
      {TODAY_GROUPS.map((group) => (
        <TodayGroup
          key={group.value}
          group={group}
          items={byGroup[group.value]}
          action={actions[group.value]}
          onMessage={onMessage}
          onEdit={onEdit}
        />
      ))}

      <ConfirmDialog
        isOpen={!!closing}
        title={closing ? `Marcar ${closing.length} contato(s) como Sem resposta?` : ''}
        variant="warning"
        confirmLabel="Marcar como Sem resposta"
        cancelLabel="Cancelar"
        onConfirm={handleClose}
        onCancel={() => !closingBusy && setClosing(null)}
        confirmDisabled={closingBusy}
        loading={closingBusy}
      >
        <Typography variant="body2" color="text.secondary">
          Saem da fila de hoje. Diferente de "Sem interesse", continuam no catálogo e podem ser procurados de novo mais para a frente.
        </Typography>
        {closeError && <Alert severity="error" sx={{ mt: 2 }}>{closeError}</Alert>}
      </ConfirmDialog>
    </Box>
  );
}

export default ProspectTodayView;
